import { useState, useCallback, useRef, useEffect } from 'react';
import { Minus, Plus, Move } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { Button } from '@/components/ui-kit/button';
import {
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui-kit/dialog';
import { Slider } from '@/components/ui-kit/slider';
import { Separator } from '@/components/ui-kit/separator';
import { cn } from '@/lib/utils';
import { useReportUnreportedError } from '@/state/query-client/hooks';

/**
 * Props for the ImageCropper component
 * @typedef {Object} ImageCropperProps
 * @property {string} image - The source URL of the image to be cropped
 * @property {() => void} onClose - Callback function called when the dialog is closed
 * @property {(croppedImage: string) => void} onCropComplete - Callback function called with the cropped image data URL
 * @property {number} [aspect=1] - The aspect ratio (width/height) for the crop area (default: 1 for square)
 */

interface ImageCropperProps {
  image: string;
  onClose: () => void;
  onCropComplete: (croppedImage: string) => void;
  aspect?: number;
  isUploading?: boolean;
  /** Defaults to round for profile avatars. Use "rect" for template thumbnails. */
  cropShape?: 'round' | 'rect';
  /** Optional fixed output width in pixels (e.g. 151 for template cards). */
  outputWidth?: number;
  /** Optional fixed output height in pixels (e.g. 302 for template cards). */
  outputHeight?: number;
  title?: string;
}

export const ImageCropper = ({
  image,
  onClose,
  onCropComplete,
  aspect = 1,
  isUploading = false,
  cropShape = 'round',
  outputWidth,
  outputHeight,
  title,
}: Readonly<ImageCropperProps>) => {
  const { t } = useTranslation();
  const reportError = useReportUnreportedError();
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLButtonElement>(null);
  const [preview, setPreview] = useState<string>('');
  const isRound = cropShape === 'round';

  const onCropChange = (location: Point) => {
    setCrop(location);
  };

  const onZoomChange = (value: number) => {
    setZoom(value);
  };

  const onCropCompleteCallback = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    if (croppedAreaPixels.width > 0 && croppedAreaPixels.height > 0) {
      setCroppedAreaPixels(croppedAreaPixels);
    }
  }, []);

  const createImage = useCallback(
    (url: string): Promise<HTMLImageElement> =>
      new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) =>
          reject(new Error(`Failed to load image: ${error.message || 'Unknown error'}`))
        );
        image.src = url;
      }),
    []
  );

  const createCanvas = (width: number, height: number): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  };

  const getCanvasContext = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get 2D context');
    }
    return ctx;
  };

  const canvasToDataURL = (canvas: HTMLCanvasElement): Promise<string> => {
    return new Promise((resolve, reject) => {
      const handleBlob = (blob: Blob | null) => {
        if (!blob) {
          try {
            resolve(canvas.toDataURL('image/jpeg', 0.92));
          } catch {
            reject(new Error('Failed to create image from canvas'));
          }
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read blob'));
        reader.readAsDataURL(blob);
      };

      canvas.toBlob(handleBlob, 'image/jpeg', 0.92);
    });
  };

  const getCroppedImg = useCallback(
    async (imageSrc: string, pixelCrop: Area): Promise<string> => {
      const sourceWidth = Math.max(1, Math.floor(pixelCrop.width || 1));
      const sourceHeight = Math.max(1, Math.floor(pixelCrop.height || 1));
      const width = Math.max(1, Math.floor(outputWidth || sourceWidth));
      const height = Math.max(1, Math.floor(outputHeight || sourceHeight));

      try {
        const image = await createImage(imageSrc);
        const canvas = createCanvas(width, height);
        const ctx = getCanvasContext(canvas);

        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(
          image,
          Math.max(0, pixelCrop.x || 0),
          Math.max(0, pixelCrop.y || 0),
          sourceWidth,
          sourceHeight,
          0,
          0,
          width,
          height
        );
        return await canvasToDataURL(canvas);
      } catch (error) {
        console.error('Error in getCroppedImg:', error);
        throw error;
      }
    },
    [createImage, outputHeight, outputWidth]
  );

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) return;

    try {
      const croppedImage = await getCroppedImg(image, croppedAreaPixels);
      onCropComplete(croppedImage);
    } catch (e) {
      reportError(e);
    }
  }, [croppedAreaPixels, getCroppedImg, image, onCropComplete, reportError]);

  useEffect(() => {
    let isMounted = true;

    const updatePreview = async () => {
      if (!croppedAreaPixels?.width || !croppedAreaPixels?.height) {
        if (isMounted) setPreview('');
        return;
      }

      try {
        const croppedPreview = await getCroppedImg(image, croppedAreaPixels);
        if (isMounted) {
          setPreview(croppedPreview);
        }
      } catch (e) {
        console.error('Error generating preview', e);
        if (isMounted) {
          setPreview('');
        }
      }
    };

    updatePreview();

    return () => {
      isMounted = false;
    };
  }, [croppedAreaPixels, image, getCroppedImg]);

  const stageClassName = cn(
    'relative overflow-hidden',
    isRound
      ? 'h-[200px] w-[200px] md:h-[312px] md:w-[312px]'
      : 'h-[302px] w-[151px] md:h-[392px] md:w-[196px]'
  );

  const previewClassName = cn(
    'overflow-hidden border-2 border-border',
    isRound
      ? 'h-[200px] w-[200px] rounded-full md:h-[312px] md:w-[312px]'
      : 'h-[302px] w-[151px] rounded-md md:h-[392px] md:w-[196px]'
  );

  return (
    <DialogContent className="grid-cols-[minmax(0,1fr)] overflow-y-auto rounded-md sm:max-w-[750px]">
      <DialogHeader>
        <DialogTitle className="text-lg font-medium">
          {title || t('EDIT_PROFILE_PICTURE')}
        </DialogTitle>
        <DialogDescription />
      </DialogHeader>
      <div className="flex w-full flex-col gap-6 md:flex-row">
        <div className="flex w-full flex-col items-center justify-center md:w-1/2 md:items-start md:justify-start">
          <h3 className="mb-4 text-sm font-semibold text-high-emphasis">{t('RESIZE_THUMBNAIL')}</h3>
          <button
            ref={containerRef}
            type="button"
            className={stageClassName}
            onMouseEnter={() => setIsDragging(true)}
            onMouseLeave={() => setIsDragging(false)}
          >
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteCallback}
              cropShape={cropShape}
              showGrid={!isRound}
              objectFit="contain"
              classes={{
                containerClassName: 'bg-transparent',
                mediaClassName: 'max-h-full max-w-full',
                cropAreaClassName: isRound
                  ? 'rounded-full border-2 border-white shadow-md'
                  : 'rounded-md border-2 border-white shadow-md',
              }}
            />
            {isDragging && (
              <div className="pointer-events-none absolute left-1/2 top-12 flex -translate-x-1/2 items-center justify-center">
                <div className="flex items-center gap-2 rounded-[4px] bg-black bg-opacity-50 px-3 py-2">
                  <Move className="h-4 w-4 text-white" />
                  <span className="text-xs text-white">{t('DRAG_TO_REPOSITION')}</span>
                </div>
              </div>
            )}
          </button>
          <div className="mt-6 flex w-full items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="w-12"
              onClick={() => setZoom((prev) => Math.max(1, prev - 0.1))}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.1}
              onValueChange={([value]) => onZoomChange(value)}
            />
            <Button
              variant="ghost"
              size="icon"
              className="w-12"
              onClick={() => setZoom((prev) => Math.min(3, prev + 0.1))}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <Separator orientation="vertical" className="hidden h-full md:block" />
        <div className="flex w-full flex-col items-center justify-center md:w-1/2 md:items-start md:justify-start">
          <h3 className="mb-4 text-sm font-semibold text-high-emphasis">{t('PREVIEW')}</h3>
          <div className={previewClassName}>
            {preview ? (
              <img src={preview} alt="Preview" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full bg-neutral-100" />
            )}
          </div>
          {!isRound ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Output size: {outputWidth ?? '—'}×{outputHeight ?? '—'}px
            </p>
          ) : null}
        </div>
      </div>
      <DialogFooter className="mt-5 flex justify-end gap-2">
        <DialogCancelButton onClick={onClose}>{t('CANCEL')}</DialogCancelButton>
        <Button
          className="bg-[#0161AB] text-white hover:bg-[#01558F]"
          onClick={handleSave}
          loading={isUploading}
          disabled={isUploading}
        >
          {isUploading ? t('UPLOADING_IMAGE') : t('UPLOAD')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
